import Box from '@material-ui/core/Box';
import LinearProgress from '@material-ui/core/LinearProgress';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles, styled as materialStyled } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import CloseIcon from '@material-ui/icons/Close';
import DeleteForeverOutlinedIcon from '@material-ui/icons/DeleteForeverOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import InfoRoundedIcon from '@material-ui/icons/InfoRounded';
import ShareIconMUI from '@material-ui/icons/Share';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Link } from '@reach/router';
import { observer, useLocalStore, useObserver } from 'mobx-react-lite';
import React, { useContext, useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components/macro';
import { iconButtonStyles } from './Button';
import { SaveChangesContext } from '../../../contexts/SaveChanges.js';
import { ThemeContext } from '../../../contexts/Theme.js';
import {
  focusRing,
  fontSize,
  fontWeight,
  layout,
  lineHeight,
  media,
  neutral,
  radius,
  shadow,
  space,
  transition,
} from '../../../theme/tokens';
import { readableTextOn } from '../../../theme/contrast';
import { Button } from './Form';
import { FormHeader } from './Form.js';

export const StaffContainer = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};
  min-height: calc(100vh - ${layout.mobileNavHeight});
  min-width: 0;
  background-color: ${neutral.canvas};
  padding: ${space[4]} ${layout.gutter} ${space[7]};

  ${media.md`
    min-height: 100vh;
  `}

  ${media.lg`
    padding: ${space[5]} ${layout.gutterWide} ${space[7]};
  `}
`;

export const StaffContainerSpaceBetween = styled(StaffContainer)`
  justify-content: space-between;
`;

// a section rendered inside a StaffContainer page, e.g. the module tabs on a student's progress
export const StaffNestedSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${space[4]};
  min-width: 0;
`;

export const StaffRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${space[3]};
  margin: ${space[1]} 0;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  ${({ isOver }) =>
    isOver &&
    css`
      background-color: ${({ theme }) => theme.hoverFill};
    `}
`;

export const StaffHeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${space[3]};
  margin: 0;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;

  ${media.md`
    flex-direction: row;
    align-items: center;
  `}
`;

export const AlignButton = styled.div`
  margin: ${space[2]};
  ${({ center }) =>
    center &&
    css`
      display: flex;
      justify-content: center;
    `};
  ${({ hoverable }) =>
    hoverable &&
    css`
      &:hover {
        cursor: pointer;
      }
    `}
`;

export const StaffHeader = styled.h1`
  margin: 0;
  font-weight: ${fontWeight.heavy};
  font-size: ${fontSize.xxl};
  line-height: ${lineHeight.tight};
  color: ${({ theme }) => theme.headerColour};
  overflow-wrap: anywhere;

  ${media.md`
    font-size: ${fontSize.display};
  `}
`;

export const StaffSubHeader = styled.h2`
  margin: 0;
  padding-left: ${space[3]};
  font-weight: ${fontWeight.bold};
  font-size: ${fontSize.lg};
  line-height: ${lineHeight.tight};
  color: ${({ theme }) => theme.subHeaderColour};
`;

export const StaffHeaderGroup = styled.div`
  border-left: 4px solid ${({ theme }) => theme.classroomHighlight};
  border-radius: 2px;
  display: flex;
  align-items: center;
  padding: 0 ${space[2]};
  ${({ isDragging }) =>
    isDragging &&
    css`
      opacity: 0.3;
    `}
`;

export const StaffHeaderCount = styled(StaffHeader).attrs(() => ({ as: 'p' }))`
  font-size: ${fontSize.display};
`;

// tab rows (segmented control), the same look as the student Learn / Tasks tabs
export const tabTrackStyles = css`
  display: flex;
  flex-wrap: wrap;
  gap: ${space[1]};
  width: fit-content;
  max-width: 100%;
  margin: ${space[1]} 0;
  padding: ${space[1]};
  border-radius: ${radius.lg};
  background: ${neutral.subtle};
`;

export const StaffTabs = styled.nav`
  ${tabTrackStyles}
`;

export const StaffActiveLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: ${layout.touchTarget};
  margin: 0;
  padding: 0 ${space[3]};
  border-radius: ${radius.md};
  font-weight: ${fontWeight.bold};
  font-size: ${fontSize.sm};

  ${media.sm`
    padding: 0 ${space[4]};
    font-size: ${fontSize.md};
  `}
  color: ${({ theme }) => theme.headerColour};
  text-decoration: none;
  white-space: nowrap;
  transition: background ${transition.fast}, color ${transition.fast};

  &:hover {
    background: ${neutral.surface};
  }
  &[data-ispartiallycurrent='true'] {
    background: ${({ theme }) => theme.lessonHighlight};
    color: ${({ theme }) =>
      readableTextOn(theme.lessonHighlight, [
        theme.textLight,
        theme.headerColour,
      ])};
    box-shadow: ${shadow.sm};
  }
  ${focusRing}
`;

export const StaffFilterLink = ({ isfirst, ...props }) => {
  const saveChangesStore = useContext(SaveChangesContext);
  const getProps = ({ isPartiallyCurrent }) => {
    return isPartiallyCurrent ? { 'data-ispartiallycurrent': true } : {};
  };
  return (
    <StaffActiveLink
      onClick={saveChangesStore.handleNavigate}
      getProps={getProps}
      isfirst={isfirst ? 'true' : ''}
      {...props}
    />
  );
};

// sections inside an opened task or target: Evidence, then Sign off
export const DetailSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};
  min-width: 0;
`;

export const DetailSectionHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${space[2]} ${space[3]};
`;

export const DetailSectionTitle = styled.h3`
  margin: 0;
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.bold};
  line-height: ${lineHeight.tight};
  color: ${({ theme }) => theme.headerColour};
`;

export const DetailSectionHint = styled.p`
  margin: 0;
  font-size: ${fontSize.sm};
  color: ${neutral.textMuted};
`;

// Sign off at the end of an open task or target. It sticks to the bottom of the screen
// while the teacher scrolls through the evidence above it, then settles in place at
// the end of the item. The negative margins reach the edges of the accordion card.
export const StickySignOff = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 2;
  margin: ${space[4]} -${space[4]} -${space[4]};
  padding: ${space[3]} ${space[4]} ${space[4]};
  border-top: 1px solid ${neutral.border};
  border-radius: 0 0 ${radius.lg} ${radius.lg};
  background: ${neutral.surface};
  box-shadow: ${shadow.up};
`;

export const SectionDivider = styled.hr`
  width: 100%;
  margin: ${space[4]} 0;
  border: none;
  border-top: 1px solid ${neutral.border};
`;

// Panels: white sections with a title row, used to group related content on a page.
//   <Panel>
//     <PanelHeader><PanelTitle>Classes</PanelTitle><PanelActions>…</PanelActions></PanelHeader>
//     …content…
//   </Panel>
export const PanelGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${space[4]};
  /* panels in the same row share a height */
  align-items: stretch;

  ${media.lg`
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `}
`;

export const Panel = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};
  min-width: 0;
  padding: ${space[4]} ${space[5]} ${space[5]};
  border: 1px solid ${neutral.border};
  border-radius: ${radius.lg};
  background: ${neutral.surface};
  box-shadow: ${shadow.sm};
`;

export const PanelHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${space[2]} ${space[3]};
  min-height: ${layout.touchTarget};
`;

export const PanelTitle = styled.h2`
  margin: 0;
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.bold};
  line-height: ${lineHeight.tight};
  color: ${({ theme }) => theme.headerColour};
`;

export const PanelActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${space[2]};
  margin-left: auto;
`;

export const PanelEmpty = styled.p`
  margin: 0;
  font-size: ${fontSize.md};
  color: ${neutral.textMuted};
`;

// label / value pairs, e.g. a person's details
export const DetailList = styled.dl`
  display: grid;
  grid-template-columns: minmax(8rem, max-content) minmax(0, 1fr);
  gap: ${space[2]} ${space[5]};
  margin: 0;
  font-size: ${fontSize.md};
  line-height: ${lineHeight.normal};

  & dt {
    font-weight: ${fontWeight.semibold};
    color: ${neutral.textMuted};
  }
  & dd {
    margin: 0;
    font-weight: ${fontWeight.semibold};
    color: ${({ theme }) => theme.headerColour};
    overflow-wrap: anywhere;
  }
`;

// Equal-width card columns for any list of StaffCards (one column on phones).
// Overrides the cards' own widths so every card in a row lines up, including
// cards wrapped in a notification badge (li > .MuiBadge-root > card).
export const staffCardGridStyles = css`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${space[4]};
  margin: 0;
  padding: 0;
  list-style: none;

  & > li,
  & > li > .MuiBadge-root,
  & > li > .MuiBadge-root > :first-child {
    width: 100%;
    max-width: none;
    min-width: 0;
    height: 100%;
    margin: 0;
  }

  ${media.sm`
    grid-template-columns: repeat(auto-fill, minmax(30rem, 1fr));
  `}
`;

export const StaffCardGrid = styled.ul`
  ${staffCardGridStyles}
`;

export const StaffCard = styled.li`
  position: relative;
  background: ${neutral.surface};
  border: 1px solid ${neutral.border};
  box-shadow: ${shadow.sm};
  border-radius: ${radius.lg};
  margin: 0 0 ${space[4]};
  padding: ${space[4]};
  width: 100%;
  min-width: 0;
  transition: box-shadow ${transition.normal};

  &:hover {
    box-shadow: ${shadow.md};
  }

  ${media.md`
    width: auto;
    min-width: 24rem;
    max-width: 52rem;
    margin: 0 ${space[4]} ${space[4]} 0;
  `}

  ${({ hasNotifications }) =>
    hasNotifications &&
    css`
      margin: 0;
    `}
`;

export const StaffFormHeaderRow = styled.header`
  background: ${({ theme }) => theme.lessonHighlight};
  border-radius: ${radius.lg} ${radius.lg} 0 0;
  width: 100%;
`;

export const StaffFormHeader = styled(FormHeader)`
  color: ${({ theme }) => theme.textLight};
  margin: ${space[4]} ${space[7]} ${space[3]};
`;

export const StaffFooterRow = styled.footer`
  display: flex;
  flex-wrap: wrap;
  gap: ${space[3]};
  border-radius: 0 0 ${radius.lg} ${radius.lg};
  border-top: 1px solid ${neutral.border};
  width: 100%;
  justify-content: space-around;
  padding: ${space[4]};
`;

export const StaffFormCloseButton = styled.button.attrs({ $tone: 'onDark' })`
  ${iconButtonStyles}
  position: absolute;
  top: ${space[2]};
  right: ${space[2]};
`;

export const StaffCloseIcon = () => <CloseIcon style={{ fontSize: '2rem' }} />;

export const StaffTextFieldInput = observer(
  ({ field, form, stylesOverride, inputMode, ...props }) => {
    const theme = useContext(ThemeContext);
    const store = useLocalStore(() => ({
      styles: {
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: neutral.borderStrong,
          },
          '&:hover fieldset': {
            borderColor: theme.buttonPrimary,
          },
          '&.Mui-focused fieldset': {
            borderColor: theme.buttonPrimary,
          },
        },
        '& label': {
          '&.Mui-focused': {
            color: theme.buttonPrimary,
          },
        },
      },
      StyledTextInput: TextField,
    }));
    if (stylesOverride) store.styles = stylesOverride;
    useEffect(() => {
      store.StyledTextInput = materialStyled(TextField)(store.styles);
    }, [theme, store]);

    return (
      <store.StyledTextInput
        {...field}
        variant="outlined"
        InputLabelProps={{
          style: {
            fontSize: '1.6rem',
          },
        }}
        InputProps={{
          style: {
            fontSize: '1.6rem',
            color: theme.headerColour,
          },
        }}
        inputProps={
          inputMode === 'numeric'
            ? {
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }
            : undefined
        }
        {...props}
      />
    );
  }
);

export const StaffMenuItem = React.forwardRef(({ ...props }, ref) => {
  const theme = useContext(ThemeContext);
  const store = useLocalStore(() => ({
    styles: {
      fontSize: '1.6rem',
      color: theme.headerColour,
    },
    StyledMenuItem: MenuItem,
  }));
  useEffect(() => {
    store.StyledMenuItem = materialStyled(MenuItem)(store.styles);
  }, [theme, store]);

  return useObserver(() => <store.StyledMenuItem {...props} />);
});

export const StaffNavButton = styled(Button).attrs(({ $variant }) => ({
  $variant: $variant || 'update',
}))``;

export const AutocompleteMui = styled(Autocomplete)`
  margin: 2em;
  width: 75%;
  font-size: 1.6rem;
  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
      margin: 0;
    `}
`;

export const StaffAutocomplete = (props) => {
  return (
    <AutocompleteMui
      {...props}
      ListboxProps={{
        style: { fontSize: '1.6rem' },
      }}
    />
  );
};

export const StaffAutocompleteInput = React.forwardRef(({ ...props }, ref) => {
  const theme = useContext(ThemeContext);
  const store = useLocalStore(() => ({
    styles: {
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: neutral.borderStrong,
        },
        '&:hover fieldset': {
          borderColor: theme.buttonPrimary,
        },
        '&.Mui-focused fieldset': {
          borderColor: theme.buttonPrimary,
        },
      },
      '& label': {
        '&.Mui-focused': {
          color: theme.buttonPrimary,
        },
        color: theme.textDark,
      },
      '& .MuiChip-label': {
        fontSize: '1.6rem',
      },
    },
    StyledTextField: TextField,
  }));
  useEffect(() => {
    store.StyledTextField = materialStyled(TextField)(store.styles);
  }, [theme, store]);

  return useObserver(() => (
    <store.StyledTextField
      {...props}
      InputProps={{
        style: {
          fontSize: '1.6rem',
        },
        ...props.InputProps,
      }}
      InputLabelProps={{
        style: {
          fontSize: '1.6rem',
        },
        ...props.InputLabelProps,
      }}
    />
  ));
});

export const PlusIcon = (props) => (
  <AddCircleOutlineIcon
    style={{ fontSize: '1.6rem', paddingLeft: '0.3em', verticalAlign: 'sub' }}
    {...props}
  />
);

export const StaffDropDown = React.forwardRef(({ ...props }, ref) => {
  const theme = useContext(ThemeContext);
  const store = useLocalStore(() => ({
    styles: {
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: neutral.borderStrong,
        },
        '&:hover fieldset': {
          borderColor: theme.buttonPrimary,
        },
        '&.Mui-focused fieldset': {
          borderColor: theme.buttonPrimary,
        },
      },
      '& label': {
        '&.Mui-focused': {
          color: theme.buttonPrimary,
        },
      },
    },
    StyledTextField: TextField,
  }));
  useEffect(() => {
    store.StyledTextField = materialStyled(TextField)(store.styles);
  }, [theme, store]);

  return useObserver(() => <store.StyledTextField {...props} />);
});

export const ProgressBarWithLabel = ({
  completionPercent,
  lessonColours,
  ...props
}) => {
  const theme = useContext(ThemeContext);
  const classes = makeStyles({
    root: {
      height: '0.8rem',
      borderRadius: radius.pill,
    },
    colorPrimary: {
      background: lessonColours ? 'rgba(255, 255, 255, 0.35)' : neutral.border,
    },
    barColorPrimary: {
      background: lessonColours ? theme.textLight : theme.buttonUpdate,
      borderRadius: radius.pill,
    },
  })();
  return (
    <Box display="flex" alignItems="center">
      <Box
        minWidth={44}
        color={lessonColours ? theme.textLight : theme.headerColour}
        fontSize={fontSize.sm}
        fontWeight="bold"
      >
        {completionPercent}%
      </Box>
      <Box width="100%">
        <LinearProgress
          variant="determinate"
          value={completionPercent}
          classes={{
            root: classes.root,
            barColorPrimary: classes.barColorPrimary,
            colorPrimary: classes.colorPrimary,
          }}
          {...props}
        />
      </Box>
    </Box>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

export const StaffLoadingContainer = styled(StaffContainer)`
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${fadeIn} 1s linear;
`;

export const ItemLink = styled.button`
  padding: 0;
  border: none;
  font-size: ${fontSize.lg};
  font-weight: bold;
  height: ${layout.touchTarget};
  width: ${layout.touchTarget};
  border-radius: 50%;
  margin: 0 ${space[1]};
  box-shadow: ${shadow.sm};
  background: ${({ theme, isSelected }) =>
    isSelected ? theme.buttonUpdate : theme.appBackground};
  color: ${({ theme, isSelected }) =>
    isSelected ? theme.textLight : theme.headerColour};
  &:hover {
    cursor: pointer;
  }
  ${({ isDragging }) =>
    isDragging &&
    css`
      opacity: 0.3;
    `}
  ${({ isOver }) =>
    isOver &&
    css`
      background: ${({ theme }) => theme.buttonUpdate};
      color: ${({ theme }) => theme.textLight};
    `}
`;

export const AddItemButton = styled(ItemLink)`
  background: ${({ theme }) => theme.buttonCreate};
  color: ${({ theme }) => theme.textLight};
  &:disabled {
    background: ${({ theme }) => theme.buttonDisabled};
    cursor: default;
  }
`;

export const Bin = (props) => (
  <DeleteForeverOutlinedIcon
    style={{
      fontSize: '2rem',
    }}
    {...props}
  />
);

export const AddItemPlusIcon = (props) => (
  <AddCircleOutlineIcon
    style={{
      fontSize: '2.4rem',
      position: 'relative',
      top: '0.14em',
    }}
    {...props}
  />
);

export const DeleteIconButton = styled.button.attrs({ $tone: 'danger' })`
  ${iconButtonStyles}
  grid-area: delete;
`;

export const ActiveButton = styled.button`
  padding: 0;
  font-weight: 600;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.textDark};
  margin: 0;
  display: flex;
  align-items: center;
  border: none;
  background: none;
  ${({ active }) =>
    active &&
    css`
      color: ${({ theme }) => theme.classroomHighlight};
    `}
`;

// Active / Inactive toggle shown as a small status chip
export const StatusChip = styled(ActiveButton)`
  flex-shrink: 0;
  gap: ${space[1]};
  min-height: 2.8rem;
  padding: 0 ${space[3]};
  border-radius: ${radius.pill};
  background: ${neutral.subtle};
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.bold};
  white-space: nowrap;
  color: ${({ active, theme }) =>
    active ? theme.classroomHighlight : neutral.textMuted};
  cursor: pointer;

  /* ActiveIcon has inline sizing meant for larger layouts */
  & > svg {
    font-size: 1rem !important;
    align-self: center !important;
  }
  &:hover {
    background: ${neutral.border};
  }
  ${focusRing}
`;

export const ActiveIcon = () => (
  <FiberManualRecordIcon
    style={{
      fontSize: '2rem',
      alignSelf: 'end',
    }}
  />
);

export const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

export const StaffSelect = observer(({ field, form, ...props }) => {
  const theme = useContext(ThemeContext);
  const store = useLocalStore(() => ({
    styles: {
      minWidth: '20rem',
      maxWidth: '100%',
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: neutral.borderStrong,
        },
        '&:hover fieldset': {
          borderColor: theme.buttonPrimary,
        },
        '&.Mui-focused fieldset': {
          borderColor: theme.buttonPrimary,
        },
      },
      '& .MuiSelect-select:focus': {
        background: 'none',
      },
      '& label': {
        '&.Mui-focused': {
          color: theme.buttonPrimary,
        },
      },
    },
    StyledTextInput: TextField,
  }));
  useEffect(() => {
    store.StyledTextInput = materialStyled(TextField)(store.styles);
  }, [theme, store]);

  return (
    <store.StyledTextInput
      {...field}
      {...props}
      variant="outlined"
      InputLabelProps={{
        style: {
          fontSize: fontSize.md,
        },
      }}
      InputProps={{
        style: {
          fontSize: fontSize.md,
          color: theme.headerColour,
          background: neutral.surface,
        },
      }}
    />
  );
});

export const InfoIcon = (props) => {
  const theme = useContext(ThemeContext);
  return (
    <InfoRoundedIcon
      style={{ fontSize: '2.5rem', color: theme.textDark }}
      {...props}
    />
  );
};

export const InfoContainer = styled.section`
  padding: ${space[4]} ${space[5]};
`;

export const InfoHeading = styled.h2`
  color: ${({ theme }) => theme.headerColour};
  font-size: ${fontSize.xl};
  line-height: ${lineHeight.tight};
`;

export const InfoText = styled.p`
  color: ${({ theme }) => theme.textDark};
  font-size: 1.8rem;
`;

export const Inline = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: ${space[3]};
  align-items: center;

  ${media.md`
    flex-direction: row;
    align-items: flex-end;
  `}
`;

export const InlineCenter = styled.div`
  display: flex;
  align-items: center;
  gap: ${space[2]};
`;

export const EditIcon = (props) => {
  return <EditOutlinedIcon {...props} style={{ fontSize: '1.6rem' }} />;
};

export const ShareIcon = (props) => (
  <ShareIconMUI style={{ fontSize: '3rem' }} {...props} />
);
